import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import api from "../../../../../lib/api";
import Button from "../../../../../components/Button";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {
    AlertTriangleIcon,
    CalendarDaysIcon,
    CheckCircle2Icon,
    EyeIcon,
    ImageIcon,
    ListChecksIcon,
    MapPinIcon,
    RocketIcon,
    UsersIcon
} from "lucide-react";
import {MarkdownDisplay} from "../../../../../components/MarkdownDisplay";
import SectionHeading from "../../../../../components/SectionHeading";
import {
    TournamentCustomField,
    TournamentResponse,
    TournamentStatus
} from "../../../../../types/tournament";
import {normalizePage} from "../../../../../types/page";
import {useCategories} from "../../../../../hooks/useTournaments";

type OutletCtx = { tournamentId: string | null; isEdit?: boolean };

function cx(...c: Array<string | false | null | undefined>) {
    return c.filter(Boolean).join(" ");
}

function formatDateTime(iso?: string | null) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
}

function Pill({ok, label}: { ok: boolean; label: string }) {
    return (
        <span
            className={cx(
                "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                ok ? "bg-emerald-50 text-emerald-700 ring-emerald-100" : "bg-gray-100 text-gray-700 ring-gray-200"
            )}
        >
            {label}
        </span>
    );
}

function Card({title, hint, children, icon, right}: {
    title: string;
    hint?: string;
    children: React.ReactNode;
    icon?: React.ReactNode;
    right?: React.ReactNode;
}) {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-start justify-between gap-6 border-b border-gray-200 bg-gray-50 px-5 py-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        {icon}
                        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    </div>
                    {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
                </div>
                {right}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

// Status transitions a business admin can trigger from the review screen.
const TRANSITIONS: { value: TournamentStatus; label: string }[] = [
    {value: "DRAFT", label: "Draft"},
    {value: "REGISTRATION_OPEN", label: "Open registration"},
    {value: "REGISTRATION_CLOSED", label: "Close registration"},
    {value: "IN_PROGRESS", label: "Mark in progress"},
    {value: "COMPLETED", label: "Mark completed"},
    {value: "CANCELLED", label: "Cancel tournament"},
];

export function ReviewPublishStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const tournamentId =
        mode === "edit"
            ? (params.tournamentId ?? outlet.tournamentId ?? null)
            : (outlet.tournamentId ?? sp.get("tournamentId") ?? null);

    const [loading, setLoading] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [unpublishing, setUnpublishing] = useState(false);
    const [transitioning, setTransitioning] = useState(false);
    const [target, setTarget] = useState<TournamentStatus>("REGISTRATION_OPEN");

    const [t, setT] = useState<TournamentResponse | null>(null);
    const [customFields, setCustomFields] = useState<TournamentCustomField[]>([]);

    // Registration now lives per play category.
    const {data: categories, refetch: refetchCategories} = useCategories(businessId ?? "", tournamentId);

    const refresh = async () => {
        if (!businessId || !tournamentId) return;
        setLoading(true);
        try {
            const baseUrl = `/api/v2/business/${businessId}/tournaments/${tournamentId}`;
            const [tRes, fieldsRes] = await Promise.all([
                api.get(baseUrl),
                api.get(`${baseUrl}/custom-fields`).catch(() => null),
            ]);

            setT(tRes.data);
            if (tRes.data?.status) setTarget(tRes.data.status as TournamentStatus);
            void refetchCategories();

            const fields = fieldsRes
                ? normalizePage<TournamentCustomField>(fieldsRes.data).items
                : (tRes.data?.customFields ?? []);
            fields.sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
            setCustomFields(fields);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to load tournament"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!businessId || !tournamentId) return;
        void refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, tournamentId]);

    const checks = useMemo(() => {
        if (!t) return null;
        const hasTitle = !!t.title?.trim();
        const hasListingImage = !!(t.imageUrl && String(t.imageUrl).trim().length > 0);
        const cats = categories ?? [];
        const hasCategories = cats.length > 0;
        // Every PAID category needs a positive entry fee.
        const feeOk = cats.every((c) => {
            const r = c.registration;
            return !r || r.entryFeeMode === "FREE" || (r.entryFeeAmount != null && r.entryFeeAmount > 0);
        });
        const readyToPublish = hasTitle && hasListingImage && hasCategories && feeOk;
        return {hasTitle, hasListingImage, hasCategories, feeOk, readyToPublish};
    }, [t, categories]);

    const canPublish = !!checks?.readyToPublish;

    const publish = async () => {
        if (!businessId || !tournamentId) return;
        if (!canPublish) {
            notify.error("Please fix the required items before publishing.");
            return;
        }
        setPublishing(true);
        try {
            await api.post(`/api/v2/business/${businessId}/tournaments/${tournamentId}/publish`);
            notify.success("Tournament published");
            await refresh();
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to publish tournament"));
        } finally {
            setPublishing(false);
        }
    };

    const unpublish = async () => {
        if (!businessId || !tournamentId) return;
        setUnpublishing(true);
        try {
            await api.post(`/api/v2/business/${businessId}/tournaments/${tournamentId}/unpublish`);
            notify.success("Tournament unpublished");
            await refresh();
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to unpublish tournament"));
        } finally {
            setUnpublishing(false);
        }
    };

    const transition = async () => {
        if (!businessId || !tournamentId) return;
        setTransitioning(true);
        try {
            await api.post(`/api/v2/business/${businessId}/tournaments/${tournamentId}/status`, null, {params: {target}});
            notify.success(`Status changed to ${target}`);
            await refresh();
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to change status"));
        } finally {
            setTransitioning(false);
        }
    };

    if (!tournamentId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Review your tournament" : "Your tournament is almost ready"}
                description="Review everything before publishing. You can always come back and edit later."
                actions={
                    <>
                        <Button type="button" variant="outline" size="md"
                                onClick={() => navigate("/business/tournaments")}>
                            Exit
                        </Button>
                        <Button type="button" variant="secondary" size="md" onClick={refresh}
                                disabled={loading || publishing || unpublishing}>
                            Refresh
                        </Button>
                        {t && (t.slug || t.id) ? (
                            <Button
                                href={`/tournaments/${encodeURIComponent(t.slug ?? t.id)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outline"
                                size="md"
                                title="Open the public tournament page in a new tab"
                            >
                                <span className="inline-flex items-center gap-2">
                                    <EyeIcon className="size-4"/>
                                    Preview
                                </span>
                            </Button>
                        ) : null}
                        {t?.published ? (
                            <Button type="button" variant="outline" size="md" onClick={unpublish}
                                    disabled={unpublishing || loading}>
                                <span className="inline-flex items-center gap-2">
                                    <RocketIcon className="size-4"/>
                                    {unpublishing ? "Unpublishing…" : "Unpublish tournament"}
                                </span>
                            </Button>
                        ) : (
                            <Button type="button" variant="primary" size="md" onClick={publish}
                                    disabled={publishing || loading || !canPublish}>
                                <span className="inline-flex items-center gap-2">
                                    <RocketIcon className="size-4"/>
                                    {publishing ? "Publishing…" : "Publish tournament"}
                                </span>
                            </Button>
                        )}
                    </>
                }
            />

            {loading || !t || !checks ? (
                <div className="py-10 text-sm text-gray-500">Loading review…</div>
            ) : (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    {/* Left: summary */}
                    <div className="lg:col-span-7 space-y-6">
                        <Card
                            title="Tournament summary"
                            icon={<CheckCircle2Icon className="size-4 text-gray-500"/>}
                            right={<Pill ok={!!t.published} label={t.published ? "Published" : "Draft"}/>}
                        >
                            <div className="space-y-4">
                                <div className="text-lg font-semibold text-gray-900">{t.title}</div>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm text-gray-700">
                                    <div>
                                        <div className="font-medium">Play categories</div>
                                        <div className="text-gray-600">{categories?.length ?? 0}</div>
                                    </div>
                                    <div>
                                        <div className="font-medium">Status</div>
                                        <div className="text-gray-600">{t.status}</div>
                                    </div>
                                    <div>
                                        <div className="font-medium">Currency</div>
                                        <div className="text-gray-600">{t.currency ?? "—"}</div>
                                    </div>
                                    <div>
                                        <div className="font-medium">Custom fields</div>
                                        <div className="text-gray-600">{customFields.length}</div>
                                    </div>
                                    <div>
                                        <div className="font-medium">Category</div>
                                        <div className="text-gray-600">
                                            {t.category ? t.category : "—"}
                                            {t.subCategory ? ` · ${t.subCategory}` : ""}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="inline-flex items-center gap-1.5 font-medium">
                                            <CalendarDaysIcon className="size-4 text-gray-400"/> Schedule
                                        </div>
                                        <div className="text-gray-600">
                                            {t.startTime ? formatDateTime(t.startTime) : "—"}
                                            {t.endTime ? ` → ${formatDateTime(t.endTime)}` : ""}
                                        </div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <div className="inline-flex items-center gap-1.5 font-medium">
                                            <MapPinIcon className="size-4 text-gray-400"/> Location
                                        </div>
                                        <div className="text-gray-600">
                                            {[t.venue, t.address].filter(Boolean).join(" · ") || "—"}
                                        </div>
                                    </div>
                                </div>

                                {t.tags?.length ? (
                                    <div>
                                        <div className="text-sm font-medium text-gray-700">Tags</div>
                                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                                            {t.tags.map((tag) => (
                                                <span key={tag}
                                                      className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-200">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}

                                {t.description ? (
                                    <div
                                        className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                                        <MarkdownDisplay value={t.description}/>
                                    </div>
                                ) : null}

                                <div className="flex justify-end">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="md"
                                        onClick={() =>
                                            mode === "edit"
                                                ? navigate(`/business/tournaments/${encodeURIComponent(tournamentId)}/edit/details`)
                                                : navigate(`/business/tournaments/new/details?tournamentId=${encodeURIComponent(tournamentId)}`)
                                        }
                                    >
                                        Edit details
                                    </Button>
                                </div>
                            </div>
                        </Card>

                        <Card
                            title="Play categories"
                            hint="Each category has its own competitor type and registration settings."
                            icon={<UsersIcon className="size-4 text-gray-500"/>}
                            right={<Pill ok={checks.hasCategories}
                                         label={`${categories?.length ?? 0} categor${(categories?.length ?? 0) === 1 ? "y" : "ies"}`}/>}
                        >
                            {(categories?.length ?? 0) === 0 ? (
                                <p className="text-sm text-gray-500">
                                    No play categories yet. Add at least one before publishing.
                                </p>
                            ) : (
                                <ul className="divide-y divide-gray-100">
                                    {[...(categories ?? [])].sort((a, b) => a.position - b.position).map((c) => {
                                        const r = c.registration;
                                        const fee = r?.entryFeeMode === "PAID"
                                            ? `${t.currency ?? ""} ${Number(r.entryFeeAmount ?? 0).toFixed(2)}`
                                            : "Free";
                                        return (
                                            <li key={c.id} className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-medium text-gray-900">{c.name}</div>
                                                    <div className="mt-0.5 text-xs text-gray-500">
                                                        {c.competitorType === "TEAM"
                                                            ? `Team${c.teamSize ? ` · ${c.teamSize} per side` : ""}`
                                                            : "Individual"}
                                                        {" · "}{c.acceptedCount} accepted
                                                    </div>
                                                </div>
                                                <div className="shrink-0 text-sm font-semibold text-gray-700">{fee}</div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            <div className="mt-4 flex justify-end">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="md"
                                    onClick={() =>
                                        mode === "edit"
                                            ? navigate(`/business/tournaments/${encodeURIComponent(tournamentId)}/edit/play-categories`)
                                            : navigate(`/business/tournaments/new/play-categories?tournamentId=${encodeURIComponent(tournamentId)}`)
                                    }
                                >
                                    Edit play categories
                                </Button>
                            </div>
                        </Card>

                        <Card
                            title="Images"
                            hint="A listing image is required to publish."
                            icon={<ImageIcon className="size-4 text-gray-500"/>}
                            right={<Pill ok={checks.hasListingImage}
                                         label={checks.hasListingImage ? "Ready" : "Missing"}/>}
                        >
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <div className="text-xs font-semibold text-gray-500">Listing image (required)</div>
                                    <div
                                        className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 aspect-square">
                                        {t.imageUrl ? (
                                            <img src={t.imageUrl} alt="Listing" className="h-full w-full object-cover"/>
                                        ) : (
                                            <div
                                                className="flex h-full items-center justify-center text-sm text-gray-500">Missing</div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-gray-500">Cover image (optional)</div>
                                    <div
                                        className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 h-48">
                                        {t.coverImageUrl ? (
                                            <img src={t.coverImageUrl} alt="Cover"
                                                 className="h-full w-full object-cover"/>
                                        ) : (
                                            <div
                                                className="flex h-full items-center justify-center text-sm text-gray-500">None</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 flex justify-end">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="md"
                                    onClick={() =>
                                        mode === "edit"
                                            ? navigate(`/business/tournaments/${encodeURIComponent(tournamentId)}/edit/images`)
                                            : navigate(`/business/tournaments/new/images?tournamentId=${encodeURIComponent(tournamentId)}`)
                                    }
                                >
                                    Edit images
                                </Button>
                            </div>
                        </Card>

                        <Card
                            title="Data collection"
                            hint="Custom fields competitors fill in when registering."
                            icon={<ListChecksIcon className="size-4 text-gray-500"/>}
                            right={<Pill ok
                                         label={`${customFields.length} field${customFields.length === 1 ? "" : "s"}`}/>}
                        >
                            {customFields.length === 0 ? (
                                <p className="text-sm text-gray-500">No custom fields. Only the competitor's name is
                                    collected.</p>
                            ) : (
                                <ul className="divide-y divide-gray-100">
                                    {customFields.map((f) => (
                                        <li key={f.id}
                                            className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
                                            <div className="min-w-0">
                                                <div
                                                    className="truncate text-sm font-medium text-gray-900">{f.label}</div>
                                                {f.type === "DROPDOWN" && f.options ? (
                                                    <div
                                                        className="mt-0.5 text-xs text-gray-500">Options: {f.options}</div>
                                                ) : null}
                                            </div>
                                            <div className="flex shrink-0 items-center gap-1.5">
                                                <span
                                                    className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-100">
                                                    {f.type}
                                                </span>
                                                {f.required ? (
                                                    <span
                                                        className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700 ring-1 ring-orange-100">
                                                        Required
                                                    </span>
                                                ) : null}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div className="mt-4 flex justify-end">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="md"
                                    onClick={() =>
                                        mode === "edit"
                                            ? navigate(`/business/tournaments/${encodeURIComponent(tournamentId)}/edit/data-collection`)
                                            : navigate(`/business/tournaments/new/data-collection?tournamentId=${encodeURIComponent(tournamentId)}`)
                                    }
                                >
                                    Edit data collection
                                </Button>
                            </div>
                        </Card>
                    </div>

                    {/* Right: checklist + status */}
                    <div className="lg:col-span-5 space-y-6">
                        <Card title="Publish checklist" hint="These items must be complete to publish."
                              icon={<CheckCircle2Icon className="size-4 text-gray-500"/>}>
                            <div className="space-y-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Details</div>
                                        <div className="text-xs text-gray-500">Tournament title</div>
                                    </div>
                                    <Pill ok={checks.hasTitle} label={checks.hasTitle ? "OK" : "Fix"}/>
                                </div>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Play categories</div>
                                        <div className="text-xs text-gray-500">At least one category is required</div>
                                    </div>
                                    <Pill ok={checks.hasCategories} label={checks.hasCategories ? "OK" : "Fix"}/>
                                </div>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Listing image</div>
                                        <div className="text-xs text-gray-500">Image shown in tournament listing</div>
                                    </div>
                                    <Pill ok={checks.hasListingImage} label={checks.hasListingImage ? "OK" : "Fix"}/>
                                </div>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-gray-900">Entry fees</div>
                                        <div className="text-xs text-gray-500">Paid categories need an amount above
                                            zero
                                        </div>
                                    </div>
                                    <Pill ok={checks.feeOk} label={checks.feeOk ? "OK" : "Fix"}/>
                                </div>

                                {!canPublish ? (
                                    <div
                                        className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                        <div className="inline-flex items-center gap-2 font-semibold">
                                            <AlertTriangleIcon className="size-4"/>
                                            Publishing is blocked until required items are complete.
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                                        <div className="inline-flex items-center gap-2 font-semibold">
                                            <CheckCircle2Icon className="size-4"/>
                                            Ready to publish.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>

                        <Card title="Status" hint="Move the tournament through its lifecycle."
                              icon={<CheckCircle2Icon className="size-4 text-gray-500"/>}>
                            <div className="space-y-3">
                                <div className="text-sm text-gray-700">
                                    Current status: <span className="font-semibold">{t.status}</span>
                                </div>
                                <select
                                    value={target}
                                    onChange={(e) => setTarget(e.target.value as TournamentStatus)}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                >
                                    {TRANSITIONS.map((tr) => (
                                        <option key={tr.value} value={tr.value}>{tr.label}</option>
                                    ))}
                                </select>
                                <Button type="button" variant="secondary" size="md" onClick={transition}
                                        disabled={transitioning} fullWidth>
                                    {transitioning ? "Updating…" : "Change status"}
                                </Button>
                            </div>
                        </Card>
                    </div>
                </div>
            )}
        </div>
    );
}
